import React, {useContext, useEffect, useState} from "react";
import {IAdServer, IAdserverOverviewData, IDataPointOverviewGeneralData} from "@leap4/entities-discrepancy-common";
import {Grid, Typography, Divider, Stack} from "@leap4/frontend";
import {metricEnumKeyMap} from "../../../../utils/enums";
import {
    extractOverviewMetrics,
    extractOverviewValues,
    getMetricChangePercent, OverviewValues
} from "./OverviewHelpers";
import {SingleMetric} from "./SingleMetric";
import {ComparisonState} from "../../../../config/generals";
import {UserViewsContext} from "../../../../contexts/userViewsContext";

export interface IOverviewGeneralInfoProps {
    pageResult: OverviewValues,
    comparisonData:ComparisonState
}

// Component for displaying just the entity info (name, adServer)
const OverviewInfo = ({pageResult}: {pageResult: OverviewValues}): JSX.Element => {
    const [initialAllMetrics] = useState(extractOverviewValues(pageResult))

    const isSame = ()=>{
        if(initialAllMetrics.entityName.toLowerCase() === initialAllMetrics.adServer?.name?.toLowerCase()) return true;
        return false;
    }

    return (
        <Grid container direction={"column"} alignItems={"center"} justifyContent={"center"} style={{padding: '15px', height: '100%'}}>
            <Grid item xs={12} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Stack 
                    direction={{ xs: 'column', md: 'row' }} 
                    spacing={2} 
                    alignItems="center"
                    justifyContent="center"
                    flexWrap="wrap"
                    divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
                >
                    <Typography variant="h5" fontWeight="bold">{initialAllMetrics.entityName.toUpperCase()}</Typography>
                    {!isSame() && <Typography variant="h6">{(initialAllMetrics.adServer as IAdServer)?.name}</Typography>}
                </Stack>
            </Grid>
        </Grid>
    )
}

// Component for displaying the metrics
const OverviewMetrics = ({pageResult, comparisonData}: IOverviewGeneralInfoProps): JSX.Element => {
    const constantGap = '10px'
    const {userViews} = useContext(UserViewsContext)
    const [initialAllMetrics] = useState(extractOverviewValues(pageResult))
    const [selectedDatesMetrics, setSelectedMetrics] = useState(extractOverviewValues(pageResult))
    const [selectedComparisonMetrics, setSelectedComparisonMetrics] = useState(extractOverviewMetrics(comparisonData.data))

    useEffect(() => {
        const {overviewViews} = userViews.activePreset
        const newSelectedMetrics = {} as any
        overviewViews.forEach(view => {
            newSelectedMetrics[view] = initialAllMetrics[view]
        })
        setSelectedMetrics(newSelectedMetrics as any)
        setSelectedComparisonMetrics(extractOverviewMetrics(comparisonData.data))
    }, [userViews, comparisonData])

    return (
        <Grid container alignItems={"center"} justifyContent={"center"} style={{padding: '15px', height: '100%'}}>
            <Grid item width={"100%"}>
                <Grid container gap={3} width={"100%"} padding={"0 40px"} height={"100%"} justifyContent={"space-around"} flexWrap={"wrap"}>
                    {Object.entries(selectedDatesMetrics).map(([metricKey, metricValue]) => (
                            <Grid item key={metricKey}>
                                <SingleMetric
                                    metricChange={getMetricChangePercent(metricValue,selectedComparisonMetrics[metricKey])}
                                    metricValue={metricValue} metricEnum={metricEnumKeyMap[metricKey]?.label || 'UNDEFINED'}/>
                            </Grid>
                        )
                    )}
                </Grid>
            </Grid>
        </Grid>
    )
}

// Combined component (for backward compatibility)
const OverviewGeneralInfo = ({pageResult,comparisonData}: IOverviewGeneralInfoProps): JSX.Element => {
    return (
        <Grid container direction={"column"} style={{height: '100%'}}>
            <OverviewInfo pageResult={pageResult} />
            <OverviewMetrics pageResult={pageResult} comparisonData={comparisonData} />
        </Grid>
    )
}


export {OverviewGeneralInfo, OverviewInfo, OverviewMetrics}

